/**
 * Compose screen — send a one-off email or SMS (mobile C6).
 *
 * Channel chooses the endpoint: Email → POST /api/communications/send-email,
 * SMS → POST /api/communications/send-sms. Subject is email-only. The recipient is a
 * free-text address (email or phone) per the work order — the backend resolves a contact
 * if matched. Sending is owner/office only; a 403 surfaces a friendly inline error.
 *
 * Pushed over the tabs on the parent (app) stack. NEVER imports @smartbox/* (invariant 1).
 * Design-system primitives only; no raw styles.
 */
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiRequestError } from '../../../src/api/client';
import {
  sendEmail,
  sendSms,
  type Channel,
  type SendResult,
} from '../../../src/api/comms';
import { Screen, Text, Card, Button, Field, Select, Badge } from '../../../src/components/ui';
import { VoiceField } from '../../../src/components/voice/VoiceField';
import { theme, type StatusKey } from '../../../src/theme';
import { extractErrorMessage, humanize } from '../../../src/lib/format';

const CHANNEL_OPTIONS: ReadonlyArray<{ value: Channel; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

function resultBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'sent':
    case 'delivered':
      return { key: 'paid', label: humanize(status) };
    case 'queued':
    case 'scheduled':
      return { key: 'sent', label: humanize(status) };
    case 'skipped':
      return { key: 'draft', label: 'Skipped' };
    case 'failed':
      return { key: 'overdue', label: 'Failed' };
    default:
      return { key: 'draft', label: humanize(status) || status };
  }
}

export default function ComposeScreen() {
  const queryClient = useQueryClient();

  const [channel, setChannel] = useState<Channel>('email');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<SendResult | null>(null);

  const sendMutation = useMutation({
    mutationFn: async (): Promise<SendResult> => {
      if (channel === 'email') {
        return sendEmail({ to: to.trim(), subject: subject.trim(), body: body.trim() });
      }
      return sendSms({ to: to.trim(), body: body.trim() });
    },
    onSuccess: (res) => {
      setResult(res);
      setFormError(null);
      void queryClient.invalidateQueries({ queryKey: ['communications'] });
    },
    onError: (err) => {
      setResult(null);
      if (err instanceof ApiRequestError && err.status === 403) {
        setFormError('Only owners and office staff can send messages from BEAM.');
        return;
      }
      setFormError(extractErrorMessage(err, 'Could not send message.'));
    },
  });

  const onSend = () => {
    setFormError(null);
    setResult(null);
    if (!to.trim()) {
      setFormError(channel === 'email' ? 'Enter a recipient email address.' : 'Enter a recipient phone number.');
      return;
    }
    if (channel === 'email' && !subject.trim()) {
      setFormError('Enter a subject for the email.');
      return;
    }
    if (!body.trim()) {
      setFormError('Enter a message body.');
      return;
    }
    sendMutation.mutate();
  };

  const isEmail = channel === 'email';
  const rb = result ? resultBadge(result.status) : null;

  return (
    <Screen scroll>
      <Text variant="title">Compose</Text>

      <Select
        label="Channel"
        options={CHANNEL_OPTIONS}
        value={channel}
        onChange={(v) => {
          setChannel(v);
          setResult(null);
          setFormError(null);
        }}
      />

      <Field
        label={isEmail ? 'To (email)' : 'To (phone)'}
        placeholder={isEmail ? 'name@example.com' : '+1 555 555 5555'}
        value={to}
        onChangeText={setTo}
        keyboardType={isEmail ? 'email-address' : 'phone-pad'}
        autoCapitalize="none"
        autoCorrect={false}
        accessibilityLabel="Recipient"
      />

      {isEmail ? (
        <VoiceField
          label="Subject"
          placeholder="e.g. Your estimate is ready"
          value={subject}
          onChangeText={setSubject}
          accessibilityLabel="Email subject"
          micAccessibilityLabel="Dictate subject"
        />
      ) : null}

      <VoiceField
        label="Message"
        placeholder="Type your message…"
        value={body}
        onChangeText={setBody}
        multiline
        numberOfLines={6}
        style={styles.multiline}
        accessibilityLabel="Message body"
        micAccessibilityLabel="Dictate message"
      />

      {formError ? (
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {formError}
          </Text>
        </Card>
      ) : null}

      {result && rb ? (
        <Card>
          <View style={styles.resultRow}>
            <Text variant="bodyStrong">Message {rb.label.toLowerCase()}</Text>
            <Badge status={rb.key} label={rb.label} />
          </View>
          {result.reason ? (
            <Text variant="small" color="secondary">
              {result.reason}
            </Text>
          ) : null}
        </Card>
      ) : null}

      <Button
        title={isEmail ? 'Send email' : 'Send SMS'}
        onPress={onSend}
        loading={sendMutation.isPending}
        disabled={sendMutation.isPending}
        accessibilityLabel="Send message"
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  multiline: {
    minHeight: theme.layout.fieldHeight * 3,
    textAlignVertical: 'top',
  },
  resultRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
});
